import { AgGridReact, AgGridReactProps } from 'ag-grid-react'
import { Box } from '@mui/material'
import { AG_GRID_THEME } from '../BulkSheet/const'
import React, { Ref, useCallback, useMemo, useRef, useState } from 'react'
import { onCellSelectionChanged } from './gridEventCallback'
import { getUrlQueryObject } from '../../../utils/urls'
import { GridOptions, GridReadyEvent } from 'ag-grid-community'
import { processDataFromClipboard } from '../commons/AgGrid'
import {
  SELECTION_BLOCK_ROW_CLASS_RULES,
  createSelectionRowFrameHandler,
} from '../commons/AgGrid/selectionRowFrame'

type Props = Omit<AgGridReactProps, 'gridOptions'> & {
  gridRef?: Ref<AgGridReact>
  gridOptions?: GridOptions
}
export const BulkSheetView = React.forwardRef<HTMLDivElement, Props>(
  ({ gridOptions, ...props }: Props, ref: React.Ref<HTMLDivElement>) => {
    // BulkSheetView 自身が theme root 要素を参照できるよう、転送 ref と内部 ref を併用する。
    // 選択枠の DOM クエリをこのグリッドのルート配下に限定するため。
    const rootElementRef = useRef<HTMLDivElement | null>(null)
    const setRootRef = useCallback(
      (element: HTMLDivElement | null) => {
        rootElementRef.current = element
        if (typeof ref === 'function') {
          ref(element)
        } else if (ref) {
          ;(ref as React.MutableRefObject<HTMLDivElement | null>).current =
            element
        }
      },
      [ref]
    )
    // 選択行の skyBlue 枠線（agGrid.scss の ::after）用。選択変更時に位置クラスを
    // DOMを作り直さず付け替える（カクつき/フォーカス・レンジ干渉を回避）。
    const refreshSelectionRowFrame = useMemo(
      () => createSelectionRowFrameHandler(() => rootElementRef.current),
      []
    )

    // 画面側 gridOptions に共通の rowClassRules と onSelectionChanged を合成する。
    const mergedGridOptions = useMemo<GridOptions | undefined>(() => {
      const screenOnSelectionChanged = gridOptions?.onSelectionChanged
      return {
        ...gridOptions,
        rowClassRules: {
          ...SELECTION_BLOCK_ROW_CLASS_RULES,
          ...gridOptions?.rowClassRules,
        },
        onSelectionChanged: event => {
          refreshSelectionRowFrame(event)
          screenOnSelectionChanged?.(event)
        },
      }
    }, [gridOptions, refreshSelectionRowFrame])

    const rowBuffer = useMemo(() => {
      const urlQueryString = getUrlQueryObject()
      return !isNaN(Number(urlQueryString?.rowBuffer))
        ? (urlQueryString.rowBuffer as number)
        : 10
    }, [])

    const [copyHeadersToClipboard, setCopyHeadersToClipboard] = useState(false)
    const onKeyDown = useCallback(
      e => {
        setCopyHeadersToClipboard(e.shiftKey)
      },
      [setCopyHeadersToClipboard]
    )

    const [sideBarConfig, setSideBarConfig] = useState({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: '',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressPivotMode: true,
            suppressValues: true,
            suppressRowGroups: true,
          },
        },
      ],
      defaultToolPanel: '',
    })

    const onGridReady = useCallback(
      (event: GridReadyEvent) => {
        if (props.onGridReady) {
          ;(props.onGridReady as (event: GridReadyEvent) => void)(event)
        }
      },
      [props.onGridReady]
    )

    return (
      <Box
        ref={setRootRef}
        className={AG_GRID_THEME}
        sx={{
          width: '100%',
          height: '100%',
          padding: '0 8px 8px',
        }}
        onKeyDown={onKeyDown}
      >
        <AgGridReact
          ref={props.gridRef}
          getRowId={params => params.data.uuid}
          getDataPath={data => data.treeValue}
          rowBuffer={rowBuffer}
          animateRows={true}
          suppressGroupRowsSticky={true}
          suppressColumnVirtualisation={true}
          rowSelection={{
            mode: 'multiRow',
            checkboxes: false,
            headerCheckbox: false,
          }}
          cellSelection={true}
          undoRedoCellEditing={true}
          suppressOverlays={[
            'noRows',
            'noMatchingRows',
            'loading',
            'exporting',
          ]}
          onCellSelectionChanged={onCellSelectionChanged}
          processDataFromClipboard={processDataFromClipboard}
          copyHeadersToClipboard={copyHeadersToClipboard}
          sideBar={sideBarConfig}
          {...(props as AgGridReactProps)}
          gridOptions={mergedGridOptions}
          theme="legacy"
          onGridReady={onGridReady}
        />
      </Box>
    )
  }
)
